// src/pages/Demand.jsx - V2 REDESIGN (Conversational Feed)
import React, { useState, useEffect, useMemo } from 'react';
import Button from '../components/customButtons/Button';
import { useAuth } from '../context/AuthContext';

// --- (DemandDetailsModal and Helper functions remain the same) ---
// (Keeping the modal here for context, but omitting the large code block)

// Component for the Demand Details Modal
const DemandDetailsModal = ({ request, onClose, onFulfill }) => {
    if (!request) return null; 

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };
    
    // Renders the modal with request details and the Fulfill button
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close demand details">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <h2 className="text-3xl font-extrabold text-custom-teal mb-6 text-center">Demand Details</h2>
                <div className="space-y-4 text-gray-700">
                    <p className="text-lg"><span className="font-semibold">Product Name:</span> {request.name}</p>
                    <p><span className="font-semibold">Type:</span> <span className="capitalize">{request.type}</span></p>
                    <p><span className="font-semibold">Quantity:</span> {request.quantity} {request.unit}</p>
                    <p><span className="font-semibold">Location:</span> {request.location}</p>
                    <p><span className="font-semibold">Urgency:</span> {request.urgencyLevel}</p>
                    {request.notes && <p><span className="font-semibold">Notes:</span> {request.notes}</p>}
                    <p><span className="font-semibold">Requested On:</span> {formatDate(request.createdAt)}</p>
                    <h3 className="text-xl font-bold text-gray-800 mt-6 mb-3 border-t pt-4">Requester</h3>
                    {request.user ? (
                        <div className="space-y-2">
                            <p><span className="font-semibold">Business:</span> {request.user.business}</p>
                            <p><span className="font-semibold">Contact:</span> {request.user.email}</p>
                        </div>
                    ) : (<p className="text-red-500">Requester details not available.</p>)}
                </div>
                <div className="mt-8 pt-4 border-t border-gray-200 flex justify-center">
                    <Button variant="primary" onClick={() => onFulfill(request)} className="px-8 py-3 text-lg">
                        I Can Fulfill This Demand!
                    </Button>
                </div>
            </div>
        </div>
    );
};
// --- END DemandDetailsModal ---

// --- NEW CONVERSATIONAL CARD COMPONENT ---
const DemandCard = ({ request, handleViewDetails }) => {
    // Determine the product type and associated color/icon
    const isInput = request.type === 'input';
    const textColor = isInput ? 'text-indigo-700' : 'text-green-700';
    const bgColor = isInput ? 'bg-indigo-50' : 'bg-green-50';
    const icon = isInput ? '🌱' : '🌾'; // Fertilizer/Seed vs. Crop

    // Helper to determine urgency level text color
    const getUrgencyClass = (level) => {
        switch (level) {
            case 'urgent': return 'text-red-600 font-bold';
            case 'high': return 'text-orange-500 font-semibold';
            default: return 'text-gray-500';
        }
    };

    return (
        <div className={`p-5 rounded-xl  transition-shadow duration-300 hover:shadow-lg border ${bgColor} border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center`}>
            
            {/* Conversational Text Section */}
            <div className="flex-1 mb-4 md:mb-0">
                <p className="text-sm text-gray-500 mb-1">
                    {icon} Demand posted by **{request.user?.business || 'A buyer'}**
                </p>
                <p className="text-lg md:text-[14px] font-semibold text-gray-800 leading-snug">
                    <span className={textColor}>
                        I require {request.quantity} {request.unit} of {request.name} </span>
                    in {request.location} with a 
                    <span className={getUrgencyClass(request.urgencyLevel)}> 
                        {' ' + request.urgencyLevel} 
                    </span> need.
                </p>
            </div>
            
            {/* Action and Details Section */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full md:w-auto">
                <Button 
                    variant="primary" 
                    onClick={() => handleViewDetails(request)} 
                    className="w-full text-lg md:text-[12px] text-white sm:w-auto px-4 py-2"
                >
                    Fulfill / View Details
                </Button>
            </div>
        </div>
    );
};


// --- NEW FILTER BAR COMPONENT (REFINED) ---
const DemandFilterBar = ({ filterType, onFilterChange }) => (
    <div className="flex flex-wrap space-x-3 mb-6">
        <Button 
            variant={filterType === 'all' ? 'primary' : 'secondary'} 
            onClick={() => onFilterChange('all')}
            className="text-sm px-4 py-2 mb-2"
        >
            All Market Demands
        </Button>
        <Button 
            variant={filterType === 'output' ? 'primary' : 'secondary'} 
            onClick={() => onFilterChange('output')}
            className="text-sm px-4 py-2 mb-2"
        >
            Crops (Output)
        </Button>
        <Button 
            variant={filterType === 'input' ? 'primary' : 'secondary'} 
            onClick={() => onFilterChange('input')}
            className="text-sm px-4 py-2 mb-2"
        >
            Inputs (Fertilizer, etc.)
        </Button>
    </div>
);


const Demand = () => {
    const { token, isAuthenticated, user, loading: authLoading } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all'); // New filter state
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [notification, setNotification] = useState(null);

    // Placeholder for sorting state (no longer used for table headers)
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [requestsPerPage] = useState(10); 
    
    // --- FETCH LOGIC (Remains the same) ---
    useEffect(() => {
        // ... (API fetch logic remains the same, fetches all requests)
        const fetchProductRequests = async () => {
            if (authLoading || !isAuthenticated || !token || !user) {
                setLoading(false);
                if (!isAuthenticated) setError("You must be logged in to view market demands.");
                return;
            }

            try {
                const response = await fetch('http://localhost:5000/api/product-requests', {
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                
                if (!response.ok) {
                    const errorText = await response.text(); 
                    let errorMessage = `HTTP error! Status: ${response.status}`;
                    try {
                        const errorJson = JSON.parse(errorText);
                        errorMessage = errorJson.message || errorMessage;
                    } catch (jsonError) { /* ignore */ }
                    throw new Error(errorMessage);
                }
                
                const data = await response.json();
                // Filter out requests made by the logged-in user AND fulfilled requests
                const initialFilteredRequests = data.filter(request => 
                    request.user && request.user._id !== user.id && request.status !== 'fulfilled'
                );
                setRequests(initialFilteredRequests);
            } catch (err) {
                console.error("Failed to fetch product requests:", err.message);
                setError(`Failed to load demands: ${err.message}. Please ensure your backend is running.`);
            } finally {
                setLoading(false);
            }
        };

        fetchProductRequests();
    }, [token, isAuthenticated, user, authLoading]);

    // --- COMBINED FILTERING & PAGINATION LOGIC (Simplified) ---
    const sortedAndFilteredRequests = useMemo(() => {
        let currentRequests = requests;

        // 1. Filtering by Type
        if (filterType !== 'all') {
            currentRequests = currentRequests.filter(req => req.type === filterType);
        }

        // 2. Sorting by date (Newest first, simplest default sort for a feed)
        return [...currentRequests].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            // 'desc' is newest first
            return dateB - dateA; 
        });
    }, [requests, filterType]);

    // Pagination logic
    const totalPages = Math.ceil(sortedAndFilteredRequests.length / requestsPerPage);

    const currentRequests = useMemo(() => {
        const indexOfLastRequest = currentPage * requestsPerPage;
        const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
        return sortedAndFilteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
    }, [currentPage, requestsPerPage, sortedAndFilteredRequests]);

    // --- HANDLERS (Remain the same) ---
    const handleFilterChange = (type) => {
        setFilterType(type);
        setCurrentPage(1); // Reset page on filter change
    };

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setIsDetailModalOpen(true);
    };

    const handleFulfillRequest = async (requestToFulfill) => {
        // ... (Fulfillment logic remains the same)
        if (!isAuthenticated || !token || !user) {
            setNotification({ type: 'error', message: 'You must be logged in to fulfill a demand.' });
            return;
        }

        try {
            const response = await fetch(`http://localhost:5000/api/product-requests/${requestToFulfill._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: 'fulfilled', fulfilledBy: user.id })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fulfill demand');
            }

            setRequests(prevRequests => prevRequests.filter(req => req._id !== requestToFulfill._id));
            setNotification({ 
                type: 'success', 
                message: "You've accepted to fulfill this demand! Our team will contact you to finalize the match." 
            });
            handleCloseDetailModal();
            setTimeout(() => { setNotification(null); }, 8000); 
        } catch (err) {
            setNotification({ type: 'error', message: err.message || 'An error occurred while fulfilling the demand.' });
        }
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedRequest(null);
    };
    
    // --- RENDER LOGIC (UPDATED) ---

    // ... (Loading and Error states remain the same) ...
    if (loading || authLoading) { /* ... loading spinner ... */ return (
        <div className="min-h-screen bg-custom-light-gray flex items-center justify-center font-inter text-custom-teal text-xl">
            <svg className="animate-spin h-8 w-8 mr-3 text-custom-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading market demands...
        </div>
    );}

    if (error) { /* ... error message ... */ return (
        <div className="min-h-screen bg-custom-light-gray flex flex-col items-center justify-center font-inter text-red-600 text-lg p-4 text-center">
            <p className="mb-4 font-bold">Error:</p>
            <p className="mb-6 max-w-md">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">Retry</Button>
        </div>
    );}


    return (
        <div className="min-h-screen bg-custom-light-gray py-12 px-4 sm:px-6 lg:px-8 font-inter">
            <div className="max-w-4xl mx-auto md:mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-3xl md:text-2xl font-extrabold text-custom-teal">
                        Market Demand Feed
                    </h1>
                </div>
                
                {/* FILTER BAR */}
                <DemandFilterBar filterType={filterType} onFilterChange={handleFilterChange} />
                
                {/* Notification Alert */}
                {notification && (
                    <div className={`my-6 p-4 rounded-lg text-center font-medium
                        ${notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {notification.message}
                    </div>
                )}
                
                {/* Empty State */}
                {sortedAndFilteredRequests.length === 0 && (
                    <div className="text-center text-gray-600 text-lg py-10 bg-white rounded-xl shadow-sm mt-8">
                        {requests.length > 0 
                            ? `No ${filterType === 'input' ? 'Input' : filterType === 'output' ? 'Crop' : ''} demands match your current filter selection.`
                            : "No new market demands have been posted yet. Check back soon!"}
                    </div>
                )}

                {/* DEMAND CARD FEED */}
                <div className="space-y-6">
                    {currentRequests.map((request) => (
                        <DemandCard 
                            key={request._id} 
                            request={request} 
                            handleViewDetails={handleViewDetails}
                        />
                    ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="mt-8 bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between rounded-xl shadow-md border border-gray-100">
                        {/* Info Text */}
                        <div className="text-sm text-gray-700 mb-3 sm:mb-0">
                            Showing <span className="font-medium">{(currentPage - 1) * requestsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * requestsPerPage, sortedAndFilteredRequests.length)}</span> of <span className="font-medium">{sortedAndFilteredRequests.length}</span> results
                        </div>

                        {/* Pagination Buttons (Keeping the original Button logic) */}
                        <div className="flex justify-end">
                            {/* ... (Previous/Next buttons and page numbers from original file) ... */}
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <Button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    variant="outline"
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M12.79 5.293a1 1 0 010 1.414L9.414 10l3.376 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                </Button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <Button
                                        key={page}
                                        onClick={() => paginate(page)}
                                        variant={currentPage === page ? 'primary' : 'outline'}
                                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${currentPage === page ? 'bg-custom-teal text-white z-10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        {page}
                                    </Button>
                                ))}
                                <Button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    variant="outline"
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M7.21 14.707a1 1 0 010-1.414L10.586 10 7.21 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                                </Button>
                            </nav>
                        </div>
                    </div>
                )}
            </div>

            {/* Demand Details Modal */}
            {isDetailModalOpen && (
                <DemandDetailsModal 
                    request={selectedRequest} 
                    onClose={handleCloseDetailModal}
                    onFulfill={handleFulfillRequest} 
                />
            )}
        </div>
    );
};

export default Demand;
